import { useCallback, useEffect, useMemo, useState } from 'react'

import { getStoreHealthCheck } from '@/api/clarity'
import { ScalrTableColumn } from '@/components/customComponents/scalrTables/tableTypes'
import { CountryFilterType } from '@/store/filterSlice'
import { StoreHealthMetricModel, StoreHealthModel } from '@/types/clarity'

const storeHealthCheckColumnsNetSales: ScalrTableColumn[] = [
  { header: 'Branch', accessorKey: 'branch', type: 'text' },
  {
    header: '% Change',
    accessorKey: 'percentChange',
    type: 'percentage',
  },
  {
    header: 'Impact',
    accessorKey: 'impact',
    type: 'money',
  },
]

const storeHealthCheckColumns: ScalrTableColumn[] = [
  { header: 'Branch', accessorKey: 'branch', type: 'text' },
  {
    header: '% Difference',
    accessorKey: 'percentChange',
    type: 'percentage',
  },
  {
    header: 'Impact',
    accessorKey: 'impact',
    type: 'money',
  },
]

export default function usePnlStoreHealthCheck(
  startDate: string,
  endDate: string,
  country: CountryFilterType
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [healthData, setHealthData] = useState<StoreHealthModel | null>(null)

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { healthMetrics } = await fetchStoreHealthCheck(
          startDate,
          endDate,
          country
        )

        setHealthData(healthMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [startDate, endDate, country])

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await fetchData()
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData])

  const refetch = useCallback(() => {
    console.log('Refetching P&L store health check data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    healthData,
    storeHealthCheckColumns,
    storeHealthCheckColumnsNetSales,
    loading,
    error,
    refetch,
  }
}

async function fetchStoreHealthCheck(
  startDate: string,
  endDate: string,
  country: CountryFilterType
): Promise<{
  healthMetrics: StoreHealthModel
}> {
  try {
    const response = await getStoreHealthCheck(startDate, endDate, country)

    const mapMetrics = (
      metrics: { branch: string; percent_change: number; impact: number }[]
    ): StoreHealthMetricModel[] =>
      metrics.map((m) => ({
        branch: m.branch,
        percentChange: m.percent_change,
        impact: m.impact,
      }))

    const healthMetrics: StoreHealthModel = {
      netSales: mapMetrics(response.net_sales),
      cogs: mapMetrics(response.cogs),
      storeLabor: mapMetrics(response.store_labor),
      opEx: mapMetrics(response.op_ex),
      ebitda: mapMetrics(response.ebitda),
    }

    return {
      healthMetrics,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}
